import React from 'react';
import { Submission } from 'types/evaluation';

interface InformationTabProps {
  submission: Submission;
}

const InformationTab: React.FC<InformationTabProps> = ({ submission }) => {
  return (
    <div>
      {/* Clean Project Header */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-baijamjuree font-bold text-gray-900 break-words">
                {(() => {
                  // Individual categories show full name, organization categories show project name
                  const individualCategories = [
                    'Young Digital Leader Award',
                    'Women in Tech Award',
                  ];
                  const isIndividualCategory = individualCategories.includes(
                    submission.category
                  );

                  if (isIndividualCategory) {
                    return (
                      submission.full_name ||
                      submission.description ||
                      'Untitled Profile'
                    );
                  } else {
                    return (
                      submission.description ||
                      submission.project_name ||
                      submission.full_name ||
                      'Untitled Project'
                    );
                  }
                })()}
              </h3>
              <p className="text-sm text-gray-600 font-baijamjuree mt-1 break-words">
                {submission.full_name} •{' '}
                {submission.org_unit || 'Unknown Organization'}
              </p>
            </div>
            <div className="flex-shrink-0 self-start">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 font-baijamjuree">
                {submission.category || 'Uncategorized'}
              </span>
            </div>
          </div>
        </div>

        {/* Information Table */}
        <div className="px-6 py-4">
          <table className="w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree w-1/4">
                  Full Name
                </td>
                <td className="py-3 text-sm text-gray-900 font-baijamjuree">
                  {submission.full_name || 'N/A'}
                </td>
              </tr>
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree">
                  Email
                </td>
                <td className="py-3 text-sm text-gray-900 font-baijamjuree">
                  {submission.email || 'N/A'}
                </td>
              </tr>
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree">
                  Job Title
                </td>
                <td className="py-3 text-sm text-gray-900 font-baijamjuree">
                  {submission.title || 'N/A'}
                </td>
              </tr>
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree">
                  Organization
                </td>
                <td className="py-3 text-sm text-gray-900 font-baijamjuree">
                  {submission.org_unit || 'N/A'}
                </td>
              </tr>
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree">
                  Pillar
                </td>
                <td className="py-3 text-sm text-gray-900 font-baijamjuree">
                  {submission.pillar || 'N/A'}
                </td>
              </tr>
              <tr className="py-3">
                <td className="py-3 text-sm font-medium text-gray-500 font-baijamjuree">
                  Category
                </td>
                <td className="py-3 text-sm font-medium text-blue-800 font-baijamjuree">
                  {submission.category || 'N/A'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Points (Enhanced PPT Transcription Display) */}
      {submission.additional_comments &&
        submission.additional_comments !== 'default' && (
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <label className="text-xl font-baijamjuree font-extrabold text-black">
                📋 Key Points
              </label>
            </div>

            {/* Enhanced Content Display */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 border-b border-gray-200">
                <p className="text-sm font-baijamjuree font-medium text-blue-800">
                  📄 Extracted from presentation materials
                </p>
              </div>

              <div className="p-6 max-h-96 overflow-y-auto">
                {(() => {
                  const content = submission.additional_comments;

                  // Parse and format the content for better readability
                  const formatContent = (text: string) => {
                    // Split by sections (=== markers)
                    const sections = text.split(/===\s*([^=]+)\s*===/);
                    const formattedSections = [];

                    for (let i = 1; i < sections.length; i += 2) {
                      const sectionTitle = sections[i]?.trim();
                      const sectionContent = sections[i + 1]?.trim();

                      if (sectionTitle && sectionContent) {
                        formattedSections.push({
                          title: sectionTitle,
                          content: sectionContent,
                        });
                      }
                    }

                    return formattedSections;
                  };

                  const sections = formatContent(content);

                  if (sections.length > 0) {
                    return (
                      <div className="space-y-6">
                        {sections.map((section, index) => (
                          <div key={index} className="space-y-3">
                            {/* Section Title */}
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-5 h-5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-medium text-xs">
                                  {index + 1}
                                </span>
                              </div>
                              <h4 className="text-sm font-baijamjuree font-medium text-gray-800">
                                {section.title}
                              </h4>
                            </div>

                            {/* Section Content */}
                            <div className="ml-7 space-y-3">
                              {section.content
                                .split('•')
                                .filter(point => point.trim())
                                .map((point, pointIndex) => {
                                  const cleanPoint = point
                                    .trim()
                                    .replace(/^\s*-\s*/, '');
                                  if (!cleanPoint) return null;

                                  return (
                                    <div
                                      key={pointIndex}
                                      className="flex items-start gap-3 group"
                                    >
                                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0 group-hover:bg-blue-600 transition-colors"></div>
                                      <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm flex-1 group-hover:text-gray-900 transition-colors">
                                        {cleanPoint}
                                      </p>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* Divider between sections */}
                            {index < sections.length - 1 && (
                              <div className="border-t border-gray-100 pt-2 mt-4"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    // Fallback for content without clear sections
                    const points = content
                      .split('•')
                      .filter(point => point.trim());

                    return (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-5 h-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">📝</span>
                          </div>
                          <h4 className="text-sm font-baijamjuree font-medium text-gray-800">
                            Summary Information
                          </h4>
                        </div>

                        <div className="ml-7 space-y-3">
                          {points.length > 1 ? (
                            points.map((point, index) => {
                              const cleanPoint = point
                                .trim()
                                .replace(/^\s*-\s*/, '');
                              if (!cleanPoint) return null;

                              return (
                                <div
                                  key={index}
                                  className="flex items-start gap-3 group"
                                >
                                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0 group-hover:bg-green-600 transition-colors"></div>
                                  <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm flex-1 group-hover:text-gray-900 transition-colors">
                                    {cleanPoint}
                                  </p>
                                </div>
                              );
                            })
                          ) : (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm whitespace-pre-wrap">
                                {content}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Footer with helpful info */}
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 font-baijamjuree flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  This information was automatically extracted from the
                  presentation materials to help with evaluation.
                </p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default InformationTab;
